import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CalendarRange, MapPin, AlertTriangle, Plus, X, Clock,
  CheckCircle2, Edit3, Trash2, BellPlus, Users, ChevronRight, Save,
  GraduationCap, Briefcase, HeartPulse, Trophy, Palette, Car,
  Utensils, Home, Moon, ShoppingBag, Tag,
} from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { HomeOSAvatar } from "./HomeOSAvatar";
import {
  SCHEDULE_CATEGORIES, SCHEDULE_DAY_START_HOUR, SCHEDULE_DAY_END_HOUR,
} from "../data/dailyScheduleData";

/* ════════════════════════════════════════════════════════════════════════
   DAILY FAMILY SCHEDULE BAR

   Desktop / tablet: one column per family member, time-axis on the left,
   coloured event blocks placed by minute-of-day. A red "now" line crosses
   all columns.

   Mobile (<768px): each member becomes a horizontal mini-timeline card.

   Persistence: state.dailyFamilySchedule via HomeOSStateProvider (localStorage).
   ════════════════════════════════════════════════════════════════════════ */

/* ── helpers ──────────────────────────────────────────────────────── */

const SCHEDULE_ICON_MAP = {
  GraduationCap, Briefcase, HeartPulse, Trophy, Palette, Car,
  Utensils, Home, Moon, ShoppingBag,
};
function ScheduleCatIcon({ name, className }) {
  const I = SCHEDULE_ICON_MAP[name] || Tag;
  return <I className={className} strokeWidth={2.25} />;
}

const HOUR_PX = 56;                         // desktop pixels per hour
const TOTAL_HOURS = SCHEDULE_DAY_END_HOUR - SCHEDULE_DAY_START_HOUR;
const TIMELINE_PX = TOTAL_HOURS * HOUR_PX;

function hhmmToMinutes(t) {
  if (!t || typeof t !== "string") return 0;
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}
function minutesToTopPx(minutes) {
  const startMin = SCHEDULE_DAY_START_HOUR * 60;
  return Math.max(0, ((minutes - startMin) / 60) * HOUR_PX);
}
function minutesToHeightPx(start, end) {
  return Math.max(20, ((end - start) / 60) * HOUR_PX);
}
function fmtTime(t) {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}
function fmtRange(s, e) { return `${fmtTime(s)} – ${fmtTime(e)}`; }

const CAT_TONES = {
  blue:    { bg: "bg-transparent dark:bg-transparent",       border: "border-[color:var(--ara-line-soft)] dark:border-blue-700",      text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",      dot: "bg-transparent" },
  violet:  { bg: "bg-transparent dark:bg-transparent",   border: "border-[color:var(--ara-line-soft)] dark:border-violet-700",  text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",  dot: "bg-transparent" },
  rose:    { bg: "bg-transparent dark:bg-transparent",       border: "border-[color:var(--ara-line-soft)] dark:border-rose-700",      text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",      dot: "bg-transparent" },
  emerald: { bg: "bg-transparent dark:bg-transparent", border: "border-[color:var(--ara-line-soft)] dark:border-emerald-700",text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",dot: "bg-transparent" },
  amber:   { bg: "bg-transparent dark:bg-transparent",     border: "border-[color:var(--ara-line-soft)] dark:border-amber-700",    text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",    dot: "bg-transparent" },
  cyan:    { bg: "bg-transparent dark:bg-transparent",       border: "border-[color:var(--ara-line-soft)] dark:border-cyan-700",      text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",      dot: "bg-transparent" },
  orange:  { bg: "bg-transparent dark:bg-transparent",   border: "border-[color:var(--ara-line-soft)] dark:border-orange-700",  text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",  dot: "bg-transparent" },
  pink:    { bg: "bg-transparent dark:bg-transparent",       border: "border-[color:var(--ara-line-soft)] dark:border-pink-700",      text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",      dot: "bg-transparent" },
  indigo:  { bg: "bg-transparent dark:bg-transparent",   border: "border-[color:var(--ara-line-soft)] dark:border-indigo-700",  text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",  dot: "bg-transparent" },
  lime:    { bg: "bg-transparent dark:bg-transparent",       border: "border-[color:var(--ara-line-soft)] dark:border-lime-700",      text: "text-lime-900 dark:text-lime-100",      dot: "bg-transparent" },
};
function toneFor(category) {
  const cat = SCHEDULE_CATEGORIES[category];
  return CAT_TONES[cat?.accent] || CAT_TONES.pink;
}

/* ── Component ─────────────────────────────────────────────────────── */

export function DailyFamilyScheduleBar() {
  const state = useHomeOSState();
  const { dailyFamilySchedule = [], members = [] } = state;
  const [now, setNow] = useState(new Date());
  const [detailEvent, setDetailEvent] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editEventId, setEditEventId] = useState(null);

  // Tick once a minute for the "now" line
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  /* People columns: render EVERY real household member (excluding pets).
     "Everyone" / "all" is NOT a column — those events are distributed into
     each real member's column as shared chips. */
  const realMembers = useMemo(
    () => (members || []).filter(m => m.ageGroup !== "Pet"),
    [members]
  );
  const realMemberIdSet = useMemo(
    () => new Set(realMembers.map(m => m.id)),
    [realMembers]
  );
  const peopleCols = realMembers;

  /* Shared events (memberId === "all" / "everyone") and orphans
     (assigned to an id that no longer exists in household). */
  const sharedEvents = useMemo(
    () => (dailyFamilySchedule || []).filter(e =>
      e.memberId === "all" || e.memberId === "everyone"
    ),
    [dailyFamilySchedule]
  );
  const unassignedEvents = useMemo(
    () => (dailyFamilySchedule || []).filter(e => {
      if (!e.memberId) return true;
      if (e.memberId === "all" || e.memberId === "everyone") return false;
      return !realMemberIdSet.has(e.memberId);
    }),
    [dailyFamilySchedule, realMemberIdSet]
  );

  /* Bucket events by column id. Shared events are pushed into every real
     member's column with a _shared flag so we can render a chip. */
  const byPerson = useMemo(() => {
    const map = {};
    realMembers.forEach(p => { map[p.id] = []; });
    (dailyFamilySchedule || []).forEach(e => {
      if (e.memberId === "all" || e.memberId === "everyone") {
        realMembers.forEach(p => map[p.id].push({ ...e, _shared: true }));
        return;
      }
      if (map[e.memberId]) map[e.memberId].push(e);
    });
    Object.keys(map).forEach(k => {
      map[k].sort((a, b) => hhmmToMinutes(a.startTime) - hhmmToMinutes(b.startTime));
    });
    return map;
  }, [dailyFamilySchedule, realMembers]);

  /* Conflicts: per person, only flag conflicts between NON-shared events
     so that whole-family events don't false-positive every column. */
  const conflictIdsByPerson = useMemo(() => {
    const out = {};
    Object.entries(byPerson).forEach(([pid, list]) => {
      out[pid] = new Set();
      const personalOnly = list.filter(ev => !ev._shared);
      for (let i = 0; i < personalOnly.length; i++) {
        for (let j = i + 1; j < personalOnly.length; j++) {
          const aStart = hhmmToMinutes(personalOnly[i].startTime);
          const aEnd   = hhmmToMinutes(personalOnly[i].endTime);
          const bStart = hhmmToMinutes(personalOnly[j].startTime);
          if (bStart < aEnd && hhmmToMinutes(personalOnly[j].endTime) > aStart) {
            out[pid].add(personalOnly[i].id);
            out[pid].add(personalOnly[j].id);
          }
        }
      }
    });
    return out;
  }, [byPerson]);

  /* Next-up event household-wide */
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nextUp = useMemo(() => {
    const upcoming = (dailyFamilySchedule || [])
      .filter(e => hhmmToMinutes(e.startTime) > nowMin && e.status !== "done")
      .sort((a, b) => hhmmToMinutes(a.startTime) - hhmmToMinutes(b.startTime));
    return upcoming[0] || null;
  }, [dailyFamilySchedule, nowMin]);

  /* Highlights — quick attention strip */
  const highlights = useMemo(() => {
    const list = [];
    const prepEvents = (dailyFamilySchedule || []).filter(e => e.needsPrep && hhmmToMinutes(e.startTime) > nowMin && e.status !== "done").slice(0, 2);
    prepEvents.forEach(e => list.push({ id: `prep-${e.id}`, tone: "amber", text: `${memberName(e.memberId, members)}: ${e.prepNote || "Prep needed"} before ${fmtTime(e.startTime)}` }));
    const anyConflict = Object.values(conflictIdsByPerson).some(set => set.size > 0);
    if (anyConflict) list.push({ id: "conflict", tone: "rose", text: "Overlapping events detected. Tap a red-edged block to check." });
    return list.slice(0, 3);
  }, [dailyFamilySchedule, nowMin, conflictIdsByPerson, members]);

  const nowTopPx = (() => {
    const startMin = SCHEDULE_DAY_START_HOUR * 60;
    const endMin   = SCHEDULE_DAY_END_HOUR * 60;
    if (nowMin < startMin || nowMin > endMin) return null;
    return ((nowMin - startMin) / 60) * HOUR_PX;
  })();

  const handleEdit = (ev) => { setDetailEvent(null); setEditEventId(ev.id); setAddOpen(true); };
  const handleAdd  = ()    => { setEditEventId(null); setAddOpen(true); };

  return (
    <section className="rounded-[28px] border border-[color:var(--ara-line-soft)] dark:border-amber-800/60     dark: dark: dark: overflow-hidden" data-testid="daily-family-schedule-bar">
      {/* Header */}
      <header className="px-5 sm:px-7 pt-5 pb-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl    flex items-center justify-center shadow-sm flex-shrink-0">
            <CalendarRange className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Today</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-gray-50 leading-tight tracking-tight">Today's Family Schedule</h2>
            <p className="text-sm font-bold text-gray-600 dark:text-gray-300">Where everyone needs to be, when, and what's coming up next.</p>
          </div>
        </div>
        <button onClick={handleAdd} className="inline-flex items-center gap-1.5 bg-transparent hover:opacity-80 text-white font-extrabold text-sm px-4 py-2.5 rounded-full shadow-md hover:shadow-none active:scale-[0.97]" data-testid="schedule-add-btn">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Add schedule item
        </button>
      </header>

      {/* Next-up + highlights */}
      <div className="px-5 sm:px-7 pb-3 flex flex-wrap gap-2">
        {nextUp ? (
          <button
            onClick={() => setDetailEvent(nextUp)}
            className="flex items-center gap-2 bg-white/85 dark:bg-slate-800/60 rounded-2xl border border-[color:var(--ara-line-soft)] dark:border-orange-800 px-3 py-2 hover:shadow-sm"
            data-testid="schedule-next-up"
          >
            <span className={`w-2.5 h-2.5 rounded-full ${toneFor(nextUp.category).dot}`} />
            <div className="text-left">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Next up</p>
              <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 leading-tight">
                {memberName(nextUp.memberId, members)} — {nextUp.title}
              </p>
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400">
                {fmtTime(nextUp.startTime)}{nextUp.location ? ` · ${nextUp.location}` : ""}{nextUp.prepNote ? ` · ${nextUp.prepNote}` : ""}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />
          </button>
        ) : (
          <p className="text-xs italic text-gray-400 px-1">Nothing scheduled later today. Quiet evening ahead.</p>
        )}
        {highlights.map(h => (
          <div key={h.id} className={`flex items-center gap-1.5 px-3 py-2 rounded-2xl text-xs font-extrabold ${h.tone === "rose" ? "bg-transparent text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)]" : "bg-transparent text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)]"}`} data-testid={`schedule-highlight-${h.id}`}>
            <AlertTriangle className="w-3 h-3" strokeWidth={3} /> {h.text}
          </div>
        ))}
      </div>

      {/* ── Desktop / tablet: grid columns ───────────────────────────── */}
      <div className="hidden md:block px-5 sm:px-7 pb-5" data-testid="schedule-desktop">
        <div className="bg-white/85 dark:bg-slate-800/60 rounded-3xl border border-[color:var(--ara-line-soft)] dark:border-orange-800 overflow-x-auto">
          <div className="min-w-fit" style={{ minWidth: `${64 + Math.max(peopleCols.length, 1) * 140}px` }}>
            {/* Column headers */}
            <div className="grid bg-transparent dark:bg-transparent border-b border-[color:var(--ara-line-soft)] dark:border-orange-800/40" style={{ gridTemplateColumns: `64px repeat(${peopleCols.length}, minmax(120px, 1fr))` }}>
            <div className="py-2.5" />
            {peopleCols.map(p => (
              <div key={p.id} className="flex flex-col items-center py-2.5 border-l border-[color:var(--ara-line-soft)] dark:border-orange-800/40" data-testid={`daily-schedule-member-${p.id}`}>
                <HomeOSAvatar member={p} size="sm" />
                <p className="text-[11px] font-extrabold text-gray-700 dark:text-gray-200 mt-1 truncate max-w-full px-1">{p.name}</p>
              </div>
            ))}
          </div>

          {/* Timeline */}
          <div className="relative grid" style={{ gridTemplateColumns: `64px repeat(${peopleCols.length}, minmax(120px, 1fr))`, height: `${TIMELINE_PX}px` }}>
            {/* Hour labels + horizontal lines */}
            <div className="relative">
              {Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => {
                const hour = SCHEDULE_DAY_START_HOUR + i;
                return (
                  <div key={i} className="absolute left-0 right-0 flex items-start justify-end pr-2" style={{ top: `${i * HOUR_PX - 6}px` }}>
                    <p className="text-[10px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] tabular-nums">{fmtTime(`${String(hour).padStart(2, "0")}:00`)}</p>
                  </div>
                );
              })}
            </div>

            {/* Member columns */}
            {peopleCols.map(p => (
              <div key={p.id} className="relative border-l border-[color:var(--ara-line-soft)] dark:border-orange-800/40" data-testid={`schedule-column-${p.id}`}>
                {/* Hour grid lines */}
                {Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => (
                  <div key={i} className="absolute left-0 right-0 border-t border-orange-50 dark:border-orange-900/30" style={{ top: `${i * HOUR_PX}px` }} />
                ))}
                {/* Event blocks */}
                {(byPerson[p.id] || []).length === 0 && (
                  <div className="absolute inset-x-2 top-3 text-[10px] font-bold text-gray-400 italic text-center" data-testid={`daily-schedule-empty-${p.id}`}>
                    Nothing scheduled yet
                  </div>
                )}
                {(byPerson[p.id] || []).map(ev => {
                  const startMin = hhmmToMinutes(ev.startTime);
                  const endMin   = hhmmToMinutes(ev.endTime);
                  const top      = minutesToTopPx(startMin);
                  const height   = minutesToHeightPx(startMin, endMin);
                  const tone     = toneFor(ev.category);
                  const isConflict = conflictIdsByPerson[p.id]?.has(ev.id);
                  const isDone   = ev.status === "done";
                  const isShared = !!ev._shared;
                  const key = `${p.id}-${ev.id}`;
                  return (
                    <button
                      key={key}
                      onClick={() => setDetailEvent(ev)}
                      className={`absolute left-1 right-1 text-left rounded-xl border-2 px-2 py-1 overflow-hidden ${tone.bg} ${isConflict ? "border-rose-500 ring-2 ring-rose-200" : tone.border} ${isDone ? "opacity-55" : ""} ${isShared ? "border-dashed" : ""} hover:shadow-md transition`}
                      style={{ top: `${top}px`, height: `${height}px` }}
                      data-testid={`schedule-event-${p.id}-${ev.id}`}
                      aria-label={`${isShared ? "Shared family event: " : ""}${ev.title} from ${fmtTime(ev.startTime)} to ${fmtTime(ev.endTime)}${ev.location ? ` at ${ev.location}` : ""}`}
                    >
                      <div className="flex items-center gap-1">
                        <p className={`text-[10px] font-extrabold tabular-nums ${tone.text}`}>{fmtTime(ev.startTime)}</p>
                        {isShared && <Users className={`w-2.5 h-2.5 ${tone.text} opacity-80`} strokeWidth={3} />}
                      </div>
                      <p className={`text-xs font-extrabold ${tone.text} truncate leading-tight ${isDone ? "line-through" : ""}`}>{ev.title}</p>
                      {ev.location && height >= 50 && <p className={`text-[10px] font-bold ${tone.text} truncate opacity-75`}>📍 {ev.location}</p>}
                      {ev.needsPrep && height >= 70 && <p className="text-[10px] font-bold text-[color:var(--ara-ink-muted)] truncate">⚠ {ev.prepNote || "Prep"}</p>}
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Now line */}
            {nowTopPx !== null && (
              <div className="absolute left-0 right-0 pointer-events-none" style={{ top: `${nowTopPx}px`, gridColumn: `1 / span ${peopleCols.length + 1}` }} data-testid="schedule-now-line">
                <div className="relative h-0">
                  <div className="absolute left-12 right-0 border-t-2 border-rose-500" />
                  <div className="absolute left-12 -top-1 w-2 h-2 rounded-full bg-transparent" />
                  <p className="absolute left-0 -top-2 text-[10px] font-extrabold text-[color:var(--ara-ink-muted)] tabular-nums">Now</p>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile: stacked per-member mini timelines ───────────────── */}
      <div className="md:hidden px-4 pb-5 space-y-3" data-testid="schedule-mobile">
        {peopleCols.map(p => {
          const list = byPerson[p.id] || [];
          return (
            <div key={p.id} className="rounded-3xl border border-[color:var(--ara-line-soft)] dark:border-orange-800 bg-white/85 dark:bg-slate-800/60 p-3" data-testid={`daily-schedule-member-${p.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <HomeOSAvatar member={p} size="xs" />
                <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50">{p.name}</p>
                <span className="ml-auto text-[10px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">{list.length} item{list.length === 1 ? "" : "s"}</span>
              </div>
              {list.length === 0 ? (
                <p className="text-xs italic text-gray-400 px-1 py-1">Nothing scheduled yet</p>
              ) : (
                <ul className="space-y-1.5">
                  {list.map(ev => {
                    const tone = toneFor(ev.category);
                    const isConflict = conflictIdsByPerson[p.id]?.has(ev.id);
                    const isShared = !!ev._shared;
                    return (
                      <li key={`${p.id}-${ev.id}`}>
                        <button onClick={() => setDetailEvent(ev)} className={`w-full text-left flex items-start gap-2 rounded-2xl border-2 px-3 py-2 ${tone.bg} ${isConflict ? "border-rose-500" : tone.border} ${isShared ? "border-dashed" : ""} ${ev.status === "done" ? "opacity-60" : ""}`} data-testid={`schedule-mobile-event-${p.id}-${ev.id}`}>
                          <span className={`mt-1 w-2 h-2 rounded-full ${tone.dot} flex-shrink-0`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1 flex-wrap">
                              <p className={`text-[11px] font-extrabold tabular-nums ${tone.text}`}>{fmtRange(ev.startTime, ev.endTime)}</p>
                              {isShared && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-[color:var(--ara-ink)] dark:text-[color:var(--ara-ink-muted)] text-[9px] font-extrabold">
                                  <Users className="w-2.5 h-2.5" strokeWidth={3} /> Family
                                </span>
                              )}
                            </div>
                            <p className={`text-sm font-extrabold ${tone.text} ${ev.status === "done" ? "line-through" : ""}`}>{ev.title}</p>
                            {ev.location && <p className={`text-[11px] font-bold ${tone.text} opacity-80`}>📍 {ev.location}</p>}
                            {ev.needsPrep && <p className="text-[11px] font-bold text-[color:var(--ara-ink-muted)]">⚠ {ev.prepNote || "Prep needed"}</p>}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Shared family events — surface them so users can see at a glance */}
      {sharedEvents.length > 0 && (
        <div className="px-5 sm:px-7 pb-4" data-testid="daily-schedule-shared-events">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mb-2 flex items-center gap-1.5">
            <Users className="w-3 h-3" strokeWidth={3} /> Shared family events ({sharedEvents.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {sharedEvents.map(ev => {
              const tone = toneFor(ev.category);
              return (
                <button
                  key={`shared-${ev.id}`}
                  onClick={() => setDetailEvent(ev)}
                  className={`inline-flex items-center gap-2 rounded-2xl border-2 border-dashed px-3 py-1.5 ${tone.bg} ${tone.border} hover:shadow-sm`}
                  data-testid={`daily-schedule-shared-event-${ev.id}`}
                >
                  <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
                  <span className={`text-[11px] font-extrabold tabular-nums ${tone.text}`}>{fmtTime(ev.startTime)}</span>
                  <span className={`text-xs font-extrabold ${tone.text}`}>{ev.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Unassigned events — events whose memberId no longer matches anyone */}
      {unassignedEvents.length > 0 && (
        <div className="px-5 sm:px-7 pb-5" data-testid="daily-schedule-unassigned">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mb-2 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" strokeWidth={3} /> Unassigned ({unassignedEvents.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {unassignedEvents.map(ev => {
              const tone = toneFor(ev.category);
              return (
                <button
                  key={`unassigned-${ev.id}`}
                  onClick={() => setDetailEvent(ev)}
                  className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-1.5 bg-white/85 dark:bg-slate-800/60 border-[color:var(--ara-line-soft)] dark:border-amber-800 hover:shadow-sm`}
                  data-testid={`daily-schedule-unassigned-event-${ev.id}`}
                >
                  <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
                  <span className="text-[11px] font-extrabold tabular-nums text-gray-700 dark:text-gray-200">{fmtTime(ev.startTime)}</span>
                  <span className="text-xs font-extrabold text-gray-900 dark:text-gray-50">{ev.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Modals */}
      <ScheduleEventDetailModal
        event={detailEvent}
        members={members}
        onClose={() => setDetailEvent(null)}
        onEdit={handleEdit}
        onMarkDone={(id) => { state.updateScheduleEvent(id, { status: "done" }); toast.success("Marked done."); setDetailEvent(null); }}
        onDelete={(id) => { if (window.confirm("Delete this schedule item?")) { state.removeScheduleEvent(id); toast.success("Deleted."); setDetailEvent(null); } }}
      />
      <ScheduleAddEditModal
        open={addOpen}
        editEventId={editEventId}
        onClose={() => { setAddOpen(false); setEditEventId(null); }}
      />
    </section>
  );
}

function memberName(id, members) {
  if (!id) return "Unassigned";
  if (id === "all") return "Everyone";
  return members.find(m => m.id === id)?.name || "Family";
}

/* ── Event detail modal ──────────────────────────────────────────── */

function ScheduleEventDetailModal({ event, members, onClose, onEdit, onMarkDone, onDelete }) {
  if (!event) return null;
  const tone = toneFor(event.category);
  const cat = SCHEDULE_CATEGORIES[event.category];
  return (
    <div className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center" data-testid="schedule-detail-modal">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-t-[28px] sm:rounded-[28px] shadow-none font-home border-2 ${tone.border} ${tone.bg}`}>
        <div className="px-5 pt-5 pb-3 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className={`text-[10px] font-extrabold uppercase tracking-[0.22em] ${tone.text} inline-flex items-center gap-1`}>
              <ScheduleCatIcon name={cat?.icon} className="w-3 h-3" />
              {event.category}
            </p>
            <h2 className={`text-2xl font-extrabold ${tone.text} leading-tight`} data-testid="schedule-detail-title">{event.title}</h2>
            <p className={`text-sm font-bold ${tone.text} mt-0.5`}>{fmtRange(event.startTime, event.endTime)} · {memberName(event.memberId, members)}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/85 text-gray-600 flex items-center justify-center" aria-label="Close" data-testid="schedule-detail-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="bg-white dark:bg-slate-900 px-5 py-4 space-y-2.5 rounded-b-[28px]">
          {event.location && (
            <p className="text-sm flex items-center gap-2 text-gray-700 dark:text-gray-200"><MapPin className="w-4 h-4 text-gray-400" strokeWidth={1.5} /> {event.location}</p>
          )}
          {event.notes && <p className="text-sm text-gray-700 dark:text-gray-300 italic leading-snug">{event.notes}</p>}
          {event.needsPrep && (
            <p className="text-sm font-extrabold text-[color:var(--ara-ink-muted)] flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.5} /> {event.prepNote || "Prep needed"}</p>
          )}
          <div className="flex flex-wrap gap-1.5 pt-2">
            <button onClick={() => onEdit?.(event)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-extrabold text-gray-700 dark:text-gray-200" data-testid="schedule-detail-edit">
              <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} /> Edit event
            </button>
            <button onClick={() => onMarkDone?.(event.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-transparent hover:opacity-80 text-white text-xs font-extrabold" data-testid="schedule-detail-mark-done">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Mark done
            </button>
            <button onClick={() => toast.success(`Reminder set for ${fmtTime(event.startTime)}.`)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-transparent hover:opacity-80 text-white text-xs font-extrabold" data-testid="schedule-detail-reminder">
              <BellPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add reminder
            </button>
            <button onClick={() => onDelete?.(event.id)} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-transparent hover:bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)] dark:border-rose-700 text-xs font-extrabold" data-testid="schedule-detail-delete">
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Add / Edit modal ───────────────────────────────────────────── */

const EMPTY_EVENT = {
  memberId: "mum", title: "", startTime: "09:00", endTime: "10:00",
  location: "", category: "Family", status: "upcoming", notes: "",
  needsPrep: false, prepNote: "",
};

function ScheduleAddEditModal({ open, editEventId, onClose }) {
  const state = useHomeOSState();
  const editing = useMemo(() => (state.dailyFamilySchedule || []).find(e => e.id === editEventId), [state.dailyFamilySchedule, editEventId]);
  const [draft, setDraft] = useState(EMPTY_EVENT);

  useEffect(() => {
    if (!open) return;
    setDraft(editing ? { ...editing } : EMPTY_EVENT);
  }, [open, editing]);

  if (!open) return null;

  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (!draft.title.trim()) { toast.error("Add a title."); return; }
    if (!draft.memberId) { toast.error("Pick a person."); return; }
    if (editing) {
      state.updateScheduleEvent(editing.id, { ...draft, title: draft.title.trim() });
      toast.success("Schedule updated.");
    } else {
      state.addScheduleEvent({ ...draft, title: draft.title.trim() });
      toast.success("Added to today's schedule.");
    }
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="schedule-add-modal">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[94vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home">
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur z-10 px-5 pt-4 pb-3 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
          <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">{editing ? "Edit schedule item" : "Add schedule item"}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-500 flex items-center justify-center" data-testid="schedule-add-close"><X className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <FormField label="Person">
            <select value={draft.memberId || ""} onChange={e => setField("memberId", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-300" data-testid="schedule-add-person">
              {(state.members || []).filter(m => m.ageGroup !== "Pet").map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              <option value="all">Everyone</option>
            </select>
          </FormField>

          <FormField label="Title">
            <input value={draft.title} onChange={e => setField("title", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-base font-bold focus:outline-none focus:ring-2 focus:ring-orange-300" placeholder="School pickup" data-testid="schedule-add-title" />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Start">
              <input type="time" value={draft.startTime} onChange={e => setField("startTime", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-300" data-testid="schedule-add-start" />
            </FormField>
            <FormField label="End">
              <input type="time" value={draft.endTime} onChange={e => setField("endTime", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-300" data-testid="schedule-add-end" />
            </FormField>
          </div>

          <FormField label="Location (optional)">
            <input value={draft.location || ""} onChange={e => setField("location", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" placeholder="e.g. Auckland Grammar" data-testid="schedule-add-location" />
          </FormField>

          <FormField label="Category">
            <select value={draft.category} onChange={e => setField("category", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-300" data-testid="schedule-add-category">
              {Object.entries(SCHEDULE_CATEGORIES).map(([id, cat]) => <option key={id} value={id}>{cat.label}</option>)}
            </select>
          </FormField>

          <FormField label="Notes (optional)">
            <textarea value={draft.notes || ""} onChange={e => setField("notes", e.target.value)} rows={2} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" data-testid="schedule-add-notes" />
          </FormField>

          <div className="flex items-center gap-2">
            <input id="needs-prep" type="checkbox" checked={!!draft.needsPrep} onChange={e => setField("needsPrep", e.target.checked)} className="w-4 h-4 rounded border-gray-300" data-testid="schedule-add-needs-prep" />
            <label htmlFor="needs-prep" className="text-sm font-bold text-gray-700 dark:text-gray-200">Needs prep</label>
          </div>

          {draft.needsPrep && (
            <FormField label="Prep note">
              <input value={draft.prepNote || ""} onChange={e => setField("prepNote", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" placeholder="e.g. Pack dance shoes" data-testid="schedule-add-prep-note" />
            </FormField>
          )}
        </div>

        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-5 py-3 border-t border-gray-100 dark:border-slate-800 flex items-center gap-2">
          <button onClick={onClose} className="px-3.5 py-2 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" data-testid="schedule-add-cancel">Cancel</button>
          <button onClick={handleSave} className="ml-auto inline-flex items-center gap-1.5 bg-transparent hover:opacity-80 text-white font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md hover:shadow-none active:scale-[0.97]" data-testid="schedule-add-save">
            <Save className="w-4 h-4" strokeWidth={1.5} /> {editing ? "Save changes" : "Add to today"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400 mb-1.5">{label}</p>
      {children}
    </div>
  );
}
